import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { InboxEntryStatus } from '@/src/components/project/Inbox2/types';
import { usePopper } from '@/src/hooks/usePopper';
import { queryClient } from '@/src/lib/react-query/react-query';
import {
  showForm,
  initialize as initializeTaskPanel,
} from '@/src/reducers/tasks/taskForm/taskForm.actions';

import CreateTaskList from '../../../../../Common/InfoPanel/CreateTask/CreateTaskList/CreateTaskList';
import { acivityLogsKeys } from '../../../../api/getActivityLogs';
import { emailEntriesKeys } from '../../../../api/getEmailEntries';
import { emailEntryKeys } from '../../../../api/getEmailEntry';
import { useEmailThreadEntryViewer } from '../../../../store/emailThreadEntryViewer.store';

const taskLinkKey = 'inbox-email-id';

const CreateTask = () => {
  const { selectedEmailEntry } = useEmailThreadEntryViewer();
  const { projectId } = useParams<{ projectId: string }>();
  const [isPopperOpen, popperAnchorEl, togglePopper] = usePopper({ anchorEl: null, open: false });
  const dispatch = useDispatch();
  const taskList = selectedEmailEntry.tasks;
  const hasTaskList = taskList.length > 0;

  const handleMouseEnter = (e) => {
    togglePopper(e);
  };

  const handleMouseLeave = () => {
    togglePopper(null);
  };

  const handleTaskCreated = useCallback(
    (tasks) => {
      const newTasks = tasks.map((task) => ({
        id: task.id,
        name: task.task,
        status: task.status,
      }));
      const newWorkstreams = tasks.map((task) => ({
        id: task.workstream.id,
        name: task.workstream.workstream,
        color: task.workstream.color,
      }));

      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId: selectedEmailEntry.inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === selectedEmailEntry.id) {
                      return {
                        ...emailEntry,
                        'allow-close': {
                          allowed: true,
                        },
                        'allowed-status-changes': [InboxEntryStatus.Closed],
                        tasks: [...(emailEntry?.tasks || []), ...newTasks],
                      };
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      queryClient.setQueryData(
        [
          ...emailEntryKeys.detail({
            emailId: selectedEmailEntry.id,
            inboxEntryId: selectedEmailEntry.inboxEntryId,
            projectId,
          }),
        ],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              'allow-close': {
                allowed: true,
              },
              'allowed-status-changes': [InboxEntryStatus.Closed],
              tasks: [...prev.data.tasks, ...newTasks],
              workstreams: prev.data.workstreams.findIndex((ws) => ws.id === newWorkstreams[0].id)
                ? prev.data.workstreams
                : [...prev.data.workstreams, newWorkstreams[0]],
            },
          };
        }
      );

      queryClient.invalidateQueries([
        ...acivityLogsKeys.detail({
          projectId,
          emailId: selectedEmailEntry.id,
          inboxEntryId: selectedEmailEntry.inboxEntryId,
        }),
      ]);
    },
    [projectId, selectedEmailEntry.inboxEntryId, selectedEmailEntry.id]
  );

  const handleTaskDeleted = useCallback(() => {
    queryClient.setQueryData(
      [
        ...emailEntryKeys.detail({
          emailId: selectedEmailEntry.id,
          inboxEntryId: selectedEmailEntry.inboxEntryId,
          projectId,
        }),
      ],
      (prev: any) => {
        return {
          ...prev,
          data: {
            ...prev.data,
            shouldWaitSocketUpdate: true,
          },
        };
      }
    );

    queryClient.invalidateQueries([
      ...acivityLogsKeys.detail({
        projectId,
        emailId: selectedEmailEntry.id,
        inboxEntryId: selectedEmailEntry.inboxEntryId,
      }),
    ]);
  }, [projectId, selectedEmailEntry.inboxEntryId, selectedEmailEntry.id]);

  const handleOpenTaskForm = useCallback(async () => {
    await dispatch(
      showForm(
        {
          linkId: selectedEmailEntry.id,
          linkKey: taskLinkKey,
        },
        {
          customSuccessToastEnabled: true,
          onCreate: handleTaskCreated,
        }
      )
    );

    $('#task-form').modal('show');
  }, [selectedEmailEntry, dispatch, handleTaskCreated]);

  const handleOpenTaskPanel = useCallback(async (taskId) => {
    await dispatch(
      initializeTaskPanel(taskId, {
        onUpdate: handleTaskCreated,
        onDelete: handleTaskDeleted,
      })
    );
    $(`#task-details`).modal('show');
  }, []);

  const handleOpenTask = (taskId) => {
    handleOpenTaskPanel(taskId);
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <ActionButton
        id={'create_task'}
        className={hasTaskList && 'tw-text-cfo-blue-700'}
        onClick={handleOpenTaskForm}
        icon={ICONS.create_task}
        tooltipSettings={{
          content: 'Create Task',
          place: 'left',
        }}
      />

      {hasTaskList && (
        <CreateTaskList
          open={isPopperOpen}
          anchorEl={popperAnchorEl}
          onShowCreateTaskModal={handleOpenTaskForm}
          taskList={taskList}
          onOpenTask={handleOpenTask}
        />
      )}
    </div>
  );
};

export default CreateTask;
